import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Sparkles,
  Info,
  Calendar,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Shield,
  AlertTriangle,
  HelpCircle,
  FileText,
  ArrowRight,
  Edit3,
  Phone,
  MessageSquare,
  Copy,
  ExternalLink,
  Target,
  Zap,
  BarChart3,
  History,
  Settings,
  Eye,
  Wallet,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  translateEnum,
  translateReadinessStatus,
  translatePlannerBucket,
  translatePlannerBucketDesc,
  buildPlanHorizonOptions,
  buildYesNoUnknownOptions,
  buildMethodOptions,
  buildPullOptions,
  buildRiskOptions,
  buildCapacityOptions,
  buildOutcomeOptions,
} from './creditBuilderI18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ===================== PLANNER BUCKET CONSTANTS =====================
const PLANNER_BUCKETS = {
  ready_now: { label: 'Ready now', color: 'success', description: 'Eligible to request immediately', maxDays: 0 },
  days_0_30: { label: '0–30 days', color: 'success', description: 'Short-term timing window', maxDays: 30 },
  days_31_90: { label: '31–90 days', color: 'gold', description: 'Standard 3-month style timing', maxDays: 90 },
  days_91_180: { label: '91–180 days', color: 'gold', description: 'Several-month timing pattern', maxDays: 180 },
  days_181_365: { label: '181–365 days', color: 'amber', description: '6+ month conservative timing', maxDays: 365 },
  manual_review: { label: 'Manual review / product-specific', color: 'neutral', description: 'Timing varies by product or profile', maxDays: null },
};

// Plan Horizon options with bucket-aware mapping
const PLAN_HORIZON_OPTIONS = [
  { value: '30', label: '30 days', days: 30 },
  { value: '60', label: '60 days', days: 60 },
  { value: '90', label: '90 days', days: 90 },
  { value: '180', label: '180 days', days: 180 },
  { value: '365', label: '365 days', days: 365 },
  { value: 'follow_bucket', label: 'Follow Planner Bucket', days: null },
];

// Auto-map Planner Bucket to default Plan Horizon
const getDefaultPlanHorizon = (bucketKey) => {
  switch (bucketKey) {
    case 'ready_now':
    case 'days_0_30':
      return '30';
    case 'days_31_90':
      return '90';
    case 'days_91_180':
      return '180';
    case 'days_181_365':
      return '365';
    case 'manual_review':
    default:
      return 'follow_bucket';
  }
};

// Get bucket key from issuer name
const getBucketKeyForIssuer = (issuerName, issuerBucketData) => {
  const issuerData = issuerBucketData.find(i => 
    issuerName?.toLowerCase().includes(i.issuer.toLowerCase().split(' ')[0])
  );
  return issuerData?.bucket || 'manual_review';
};

// Check if plan horizon is shorter than bucket
const isHorizonShorterThanBucket = (horizonValue, bucketKey) => {
  if (horizonValue === 'follow_bucket') return false;
  const bucket = PLANNER_BUCKETS[bucketKey];
  if (!bucket || bucket.maxDays === null) return false;
  const horizonDays = parseInt(horizonValue);
  return horizonDays < bucket.maxDays;
};

// Get horizon days value
const getHorizonDays = (horizonValue, bucketKey) => {
  if (horizonValue === 'follow_bucket') {
    const bucket = PLANNER_BUCKETS[bucketKey];
    return bucket?.maxDays || 90;
  }
  return parseInt(horizonValue);
};

const VERIFICATION_LEVELS = {
  official: { label: 'Officially verified', color: 'success', description: 'Confirmed by issuer documentation or official communication' },
  likely: { label: 'Likely based on official/request-flow evidence', color: 'gold', description: 'Strong internal evidence from request patterns' },
  community: { label: 'Community-supported', color: 'amber', description: 'Based on community reports and datapoints' },
  needs_reconfirmation: { label: 'Needs reconfirmation', color: 'neutral', description: 'Older data or uncertain source' },
};

const ISSUER_BUCKET_DATA = [
  {
    issuer: 'American Express',
    bucket: 'days_91_180',
    publicRule: '61+ days (same card 91+)',
    pullConfidence: { level: 'likely', text: 'Soft / Mixed' },
    autoIncreaseConfidence: { level: 'official', text: 'Yes' },
    verificationStatus: 'community',
    uiLabel: '91-day style',
    notes: '3X CLI rule may apply across cards. Product families may vary.',
  },
  {
    issuer: 'Navy Federal',
    bucket: 'days_91_180',
    publicRule: '91 first / 181+ subsequent',
    pullConfidence: { level: 'official', text: 'Soft' },
    autoIncreaseConfidence: { level: 'official', text: 'Yes' },
    verificationStatus: 'official',
    uiLabel: '91 first / 6mo subsequent',
    notes: 'First CLI at 91 days, subsequent increases may need 6 months.',
  },
  {
    issuer: 'Capital One',
    bucket: 'days_91_180',
    publicRule: 'Several months',
    pullConfidence: { level: 'official', text: 'Hard' },
    autoIncreaseConfidence: { level: 'likely', text: 'Periodic' },
    verificationStatus: 'official',
    uiLabel: 'Several months (hard pull)',
    notes: 'Hard pull confirmed. Automatic increases available for some products.',
  },
  {
    issuer: 'Chase',
    bucket: 'days_181_365',
    publicRule: '6 months minimum',
    pullConfidence: { level: 'official', text: 'Soft' },
    autoIncreaseConfidence: { level: 'community', text: 'Rare' },
    verificationStatus: 'likely',
    uiLabel: '6-month style',
    notes: 'Sapphire products may have different internal thresholds.',
  },
  {
    issuer: 'Discover',
    bucket: 'days_91_180',
    publicRule: '6 months after open or last increase',
    pullConfidence: { level: 'likely', text: 'Soft' },
    autoIncreaseConfidence: { level: 'community', text: 'Yes' },
    verificationStatus: 'likely',
    uiLabel: '91–180 days',
    notes: 'Student and secured products may differ.',
  },
  {
    issuer: 'Citi',
    bucket: 'days_181_365',
    publicRule: '6 months typical',
    pullConfidence: { level: 'community', text: 'Soft' },
    autoIncreaseConfidence: { level: 'community', text: 'Varies' },
    verificationStatus: 'community',
    uiLabel: '6-month style',
    notes: 'Product families vary significantly. DoubleCash vs Prestige may differ.',
  },
  {
    issuer: 'Bank of America',
    bucket: 'days_91_180',
    publicRule: '90+ days typical',
    pullConfidence: { level: 'likely', text: 'Soft' },
    autoIncreaseConfidence: { level: 'community', text: 'Varies' },
    verificationStatus: 'community',
    uiLabel: '90-day style',
    notes: 'Preferred Rewards status may influence timing and approval.',
  },
  {
    issuer: 'Wells Fargo',
    bucket: 'days_181_365',
    publicRule: '6–12 months',
    pullConfidence: { level: 'community', text: 'Soft' },
    autoIncreaseConfidence: { level: 'community', text: 'Rare' },
    verificationStatus: 'needs_reconfirmation',
    uiLabel: '6+ month conservative',
    notes: 'Generally conservative. Longer account history helps.',
  },
  {
    issuer: 'US Bank',
    bucket: 'days_181_365',
    publicRule: '6 months',
    pullConfidence: { level: 'community', text: 'Soft' },
    autoIncreaseConfidence: { level: 'needs_reconfirmation', text: 'Unknown' },
    verificationStatus: 'community',
    uiLabel: '6-month style',
    notes: 'Having a banking relationship may help.',
  },
  {
    issuer: 'Barclays',
    bucket: 'days_181_365',
    publicRule: '6 months typical',
    pullConfidence: { level: 'community', text: 'Soft' },
    autoIncreaseConfidence: { level: 'needs_reconfirmation', text: 'Varies' },
    verificationStatus: 'needs_reconfirmation',
    uiLabel: '6-month style',
    notes: 'Co-branded cards may have different policies.',
  },
  {
    issuer: 'Synchrony',
    bucket: 'manual_review',
    publicRule: 'Varies by store card',
    pullConfidence: { level: 'community', text: 'Soft' },
    autoIncreaseConfidence: { level: 'likely', text: 'Common' },
    verificationStatus: 'needs_reconfirmation',
    uiLabel: 'Product-specific',
    notes: 'Store card policies vary by retailer. Automatic increases common.',
  },
  {
    issuer: 'Other Issuers',
    bucket: 'manual_review',
    publicRule: 'Not publicly confirmed',
    pullConfidence: { level: 'needs_reconfirmation', text: 'Unknown' },
    autoIncreaseConfidence: { level: 'needs_reconfirmation', text: 'Unknown' },
    verificationStatus: 'needs_reconfirmation',
    uiLabel: 'Manual review needed',
    notes: 'Verify with issuer directly before relying on timing assumptions.',
  },
];

// ===================== HELPER FUNCTIONS =====================
const formatCurrency = (amount, decimals = 0) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount || 0);
};

const formatPercent = (value, decimals = 0) => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(decimals)}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

// ===================== SOFT BADGE =====================
const SoftBadge = ({ children, variant = 'neutral', className = '' }) => {
  const variants = {
    success: 'bg-[#e8f8ef] dark:bg-[rgba(16,185,129,0.20)] text-[#0f6a47] dark:text-emerald-300 border-[rgba(17,106,71,0.10)] dark:border-[rgba(16,185,129,0.25)]',
    gold: 'bg-[#fdf6e8] dark:bg-[rgba(245,158,11,0.20)] text-[#b28a34] dark:text-amber-300 border-[rgba(178,138,52,0.10)] dark:border-[rgba(245,158,11,0.25)]',
    neutral: 'bg-[#f4f7f5] dark:bg-[rgba(142,218,182,0.12)] text-[#546a5d] dark:text-[#C7DDD0] border-[rgba(84,106,93,0.10)] dark:border-[rgba(142,218,182,0.20)]',
    danger: 'bg-[#fff1f1] dark:bg-[rgba(239,68,68,0.20)] text-[#b94040] dark:text-red-300 border-[rgba(185,64,64,0.10)] dark:border-[rgba(239,68,68,0.25)]',
    amber: 'bg-[#fff8ea] dark:bg-[rgba(245,158,11,0.20)] text-[#b7791f] dark:text-amber-300 border-[rgba(183,121,31,0.10)] dark:border-[rgba(245,158,11,0.25)]',
    outline: 'bg-[rgba(255,255,255,0.7)] dark:bg-[rgba(10,38,28,0.50)] text-[#0f6a47] dark:text-emerald-300 border-[rgba(17,106,71,0.10)] dark:border-[rgba(142,218,182,0.20)]',
  };
  
  return (
    <span className={`inline-flex items-center justify-center gap-[7px] px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap border ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// ===================== METRIC CARD =====================
const MetricCard = ({ title, value, subtitle, trend, trendVariant = 'green' }) => {
  const trendColors = {
    green: 'bg-[#e8f8ef] dark:bg-[rgba(16,185,129,0.18)] text-[#0f6a47] dark:text-emerald-300',
    gold: 'bg-[#fdf6e8] dark:bg-[rgba(245,158,11,0.18)] text-[#b28a34] dark:text-amber-300',
    red: 'bg-[#fff1f1] dark:bg-[rgba(239,68,68,0.18)] text-[#b94040] dark:text-red-300',
  };
  
  return (
    <div className="p-[18px] rounded-[20px] bg-gradient-to-b from-[rgba(255,255,255,0.98)] to-[rgba(248,252,249,0.96)] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
      <div className="flex items-center justify-between gap-[10px] mb-[10px]">
        <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] font-semibold">{title}</div>
        {trend && (
          <div className={`px-[10px] py-[7px] rounded-full text-xs font-bold ${trendColors[trendVariant]}`}>
            {trend}
          </div>
        )}
      </div>
      <div className="text-[30px] leading-none font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.03em] mb-2">
        {value}
      </div>
      {subtitle && (
        <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] leading-[1.5]">{subtitle}</div>
      )}
    </div>
  );
};

// ===================== SCORE RING =====================
const ScoreRing = ({ score, size = 98, label = 'Ready' }) => {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  let ringColor = '#19a56d'; // green
  if (score < 40) ringColor = '#b94040'; // red
  else if (score < 60) ringColor = '#b7791f'; // amber
  else if (score < 80) ringColor = '#c79b3b'; // gold
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="rgba(17,106,71,0.10)"
          className="dark:stroke-[rgba(142,218,182,0.15)]"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke={ringColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-[1.05]">
        <span className="text-2xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.03em]">{score}</span>
        <span className="text-[11px] text-[#7c9888] dark:text-[#93AE9F] mt-1 font-bold uppercase tracking-[0.03em]">{label}</span>
      </div>
    </div>
  );
};

// ===================== PROGRESS CARD =====================
const ProgressCard = ({ title, value, percent, total, subtitle }) => (
  <div className="p-[18px] rounded-[20px] bg-gradient-to-b from-[#f7fcf9] to-white dark:from-[rgba(10,38,28,0.75)] dark:to-[rgba(8,32,24,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
    <div className="flex items-center justify-between gap-3 mb-[14px]">
      <div className="text-[15px] font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">{title}</div>
      <SoftBadge variant="success">{value}</SoftBadge>
    </div>
    <div className="w-full h-[10px] rounded-full bg-[#ebf3ee] dark:bg-[rgba(142,218,182,0.15)] overflow-hidden mb-2">
      <div 
        className="h-full rounded-full bg-gradient-to-r from-[#19a56d] to-[#27c57f]"
        style={{ width: `${percent}%`, transition: 'width 0.5s ease-out' }}
      />
    </div>
    <div className="flex items-center justify-between gap-[10px] text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] text-xs">
      <span>{subtitle}</span>
      {total && <span>{total}</span>}
    </div>
  </div>
);

// ===================== SIGNAL ROW =====================
const SignalRow = ({ variant = 'success', label, children }) => {
  const variants = {
    success: 'bg-[#eefaf3] dark:bg-[rgba(16,185,129,0.12)] text-[#0f6a47] dark:text-emerald-300 border-[rgba(17,106,71,0.08)] dark:border-[rgba(16,185,129,0.15)]',
    warning: 'bg-[#fff8ea] dark:bg-[rgba(245,158,11,0.12)] text-[#8a5a12] dark:text-amber-300 border-[rgba(183,121,31,0.08)] dark:border-[rgba(245,158,11,0.15)]',
    danger: 'bg-[#fff1f1] dark:bg-[rgba(239,68,68,0.12)] text-[#983a3a] dark:text-red-300 border-[rgba(185,64,64,0.08)] dark:border-[rgba(239,68,68,0.15)]',
  };
  
  return (
    <div className={`flex items-start justify-between gap-3 p-3 rounded-[14px] border text-[13px] leading-[1.55] ${variants[variant]}`}>
      <div><strong>{label}:</strong> {children}</div>
    </div>
  );
};

// ===================== LIST ROW =====================
const ListRow = ({ title, children, badge, badgeVariant = 'neutral' }) => (
  <div className="flex items-start justify-between gap-3 p-[13px_14px] rounded-[15px] bg-[#f4fbf7] dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)] text-[13px] leading-[1.55]">
    <div>
      <strong className="block text-[#0b4f35] dark:text-[#EAF7F0] mb-1">{title}</strong>
      <span className="text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F]">{children}</span>
    </div>
    {badge && <SoftBadge variant={badgeVariant}>{badge}</SoftBadge>}
  </div>
);

// ===================== FIELD INPUT =====================
const FieldInput = ({ label, readonly, value, onChange, type = 'text', placeholder, options, full, ...props }) => {
  const baseClasses = "w-full border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] rounded-[14px] p-[12px_13px] outline-none text-sm font-normal text-[#133222] dark:text-[#EAF7F0]";
  const readonlyClasses = readonly ? "bg-[#f3f8f5] dark:bg-[rgba(8,32,24,0.50)] text-[#496355] dark:text-[#93AE9F]" : "bg-[#f8fcfa] dark:bg-[rgba(10,38,28,0.65)]";
  
  return (
    <div className={`flex flex-col gap-[7px] ${full ? 'col-span-full' : ''}`}>
      <label className="text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold">{label}</label>
      {options ? (
        <select 
          value={value} 
          onChange={onChange} 
          disabled={readonly}
          className={`${baseClasses} ${readonlyClasses}`}
          {...props}
        >
          {options.map((opt, i) => (
            <option key={i} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea 
          value={value} 
          onChange={onChange} 
          readOnly={readonly}
          placeholder={placeholder}
          className={`${baseClasses} ${readonlyClasses} min-h-[106px] resize-y`}
          {...props}
        />
      ) : (
        <input 
          type={type}
          value={value} 
          onChange={onChange} 
          readOnly={readonly}
          placeholder={placeholder}
          className={`${baseClasses} ${readonlyClasses}`}
          {...props}
        />
      )}
    </div>
  );
};

// ===================== PLANNER TABS =====================
const PlannerTabs = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex gap-[10px] flex-wrap my-[18px_0_16px] overflow-x-auto">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className={`border rounded-full px-[14px] py-[10px] font-bold text-[13px] cursor-pointer whitespace-nowrap transition-all ${
          activeTab === tab.id 
            ? 'bg-gradient-to-b from-[#e9f8ef] to-[#dff5e8] dark:from-[rgba(16,185,129,0.25)] dark:to-[rgba(16,185,129,0.15)] text-[#0f6a47] dark:text-emerald-300 border-[rgba(17,106,71,0.14)] dark:border-[rgba(16,185,129,0.25)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] dark:shadow-none'
            : 'bg-[rgba(255,255,255,0.86)] dark:bg-[rgba(10,38,28,0.65)] text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)]'
        }`}
        data-testid={`tab-${tab.id}`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// ===================== CLI SCRIPT BOX =====================
const CLIScriptBox = ({ account, onSaveOutcome }) => {
  const { t } = useI18n();
  const AC = 'creditBuilder.cliPlanner.accountCard';
  const [outcome, setOutcome] = useState({
    date_contacted: new Date().toISOString().split('T')[0],
    method_used: 'Phone',
    rep_name: '',
    pull_type_result: 'Unknown',
    outcome: 'Pending',
    approved_amount: '',
    counter_offer: '',
    follow_up_date: '',
    notes_after_call: '',
  });
  
  const scriptText = `Hi, I'm calling to request a credit limit increase on this account. Before we proceed, can you please confirm whether this request would involve a soft pull or a hard pull? Also, my income and overall financial profile have improved since my last review, and I'd like to make sure my current income is updated on file before any limit increase review. My spending behavior has changed recently. I'm using this account more actively, paying on time, and managing balances more consistently. I'd like to know whether my account is eligible for a review and what amount I may qualify for.`;
  
  const copyScript = () => {
    navigator.clipboard.writeText(scriptText).then(() => {
      toast.success(t('creditBuilder.cliPlanner.toasts.scriptCopied'));
    }).catch(() => {
      toast.error(t('creditBuilder.cliPlanner.toasts.scriptCopyFailed'));
    });
  };
  
  return (
    <div className="p-[18px] rounded-[20px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
      <h5 className="m-0 text-[#0b4f35] dark:text-[#EAF7F0] text-[17px] tracking-[-0.02em] font-extrabold">{t(`${AC}.script.title`)}</h5>
      <p className="text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] text-[13px] leading-[1.6] mt-2 mb-0">
        {t(`${AC}.script.subtitle`)}
      </p>
      
      <div className="p-4 rounded-[16px] bg-[#f6fbf8] dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)] text-[#133222] dark:text-[#C7DDD0] leading-[1.72] text-sm my-[14px]">
        {scriptText}
      </div>
      
      <div className="flex gap-[10px] flex-wrap mb-[14px]">
        <Button onClick={copyScript} className="gap-2 bg-gradient-to-br from-[#11875a] to-[#27c57f] text-white font-bold shadow-[0_12px_30px_rgba(17,135,90,0.22)]">
          <Copy size={14} />
          {t(`${AC}.script.copyScript`)}
        </Button>
        <Button variant="outline" className="gap-2 border-[rgba(10,95,62,0.16)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
          <Phone size={14} />
          {t(`${AC}.script.phoneReady`)}
        </Button>
        <Button variant="outline" className="gap-2 border-[rgba(10,95,62,0.16)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
          <MessageSquare size={14} />
          {t(`${AC}.script.secureMessageVariant`)}
        </Button>
        <Button onClick={onSaveOutcome} className="gap-2 bg-gradient-to-b from-[#fff8eb] to-[#fdf2dc] dark:from-[rgba(245,158,11,0.20)] dark:to-[rgba(245,158,11,0.12)] text-[#9b7427] dark:text-amber-300 border border-[rgba(178,138,52,0.16)] dark:border-[rgba(245,158,11,0.25)]">
          <FileText size={14} />
          {t(`${AC}.script.saveOutcome`)}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-0">
        <FieldInput label={t(`${AC}.script.dateContacted`)} type="date" value={outcome.date_contacted} onChange={(e) => setOutcome(prev => ({...prev, date_contacted: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.methodUsed`)} options={buildMethodOptions(t, AC)} value={outcome.method_used} onChange={(e) => setOutcome(prev => ({...prev, method_used: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.repName`)} placeholder={t(`${AC}.script.optional`)} value={outcome.rep_name} onChange={(e) => setOutcome(prev => ({...prev, rep_name: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.pullTypeResult`)} options={buildPullOptions(t, AC).filter((o) => o.value !== 'Soft / Mixed')} value={outcome.pull_type_result} onChange={(e) => setOutcome(prev => ({...prev, pull_type_result: e.target.value}))} />
        
        <FieldInput label={t(`${AC}.script.outcome`)} options={buildOutcomeOptions(t, AC)} value={outcome.outcome} onChange={(e) => setOutcome(prev => ({...prev, outcome: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.approvedAmount`)} placeholder="$0" value={outcome.approved_amount} onChange={(e) => setOutcome(prev => ({...prev, approved_amount: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.counterOffer`)} placeholder="$0" value={outcome.counter_offer} onChange={(e) => setOutcome(prev => ({...prev, counter_offer: e.target.value}))} />
        <FieldInput label={t(`${AC}.script.followUpDate`)} type="date" value={outcome.follow_up_date} onChange={(e) => setOutcome(prev => ({...prev, follow_up_date: e.target.value}))} />
        
        <FieldInput full label={t(`${AC}.script.notesAfterCall`)} type="textarea" value={outcome.notes_after_call} onChange={(e) => setOutcome(prev => ({...prev, notes_after_call: e.target.value}))} placeholder="Rep confirmed profile review is available after income update." />
      </div>
      
      <div className="mt-4 p-[14px_16px] rounded-[16px] bg-gradient-to-b from-[rgba(255,255,255,0.9)] to-[rgba(247,251,248,0.9)] border border-[rgba(10,95,62,0.10)] text-[#5b7666] dark:text-[#C7DDD0] text-xs leading-[1.6]">
        {t(`${AC}.script.disclaimer`)}
      </div>
    </div>
  );
};

// ===================== EMPTY STATE =====================
const EmptyState = ({ onAddCard, onQuickAdd }) => {
  const { t } = useI18n();
  const CC = 'creditBuilder.creditCards';
  const CP = 'creditBuilder.cliPlanner';
  return (
  <div className="p-[34px_28px] rounded-[26px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(17,106,71,0.09)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_16px_38px_rgba(11,79,53,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.30)] text-center">
    <div className="w-[84px] h-[84px] rounded-[24px] grid place-items-center mx-auto mb-[18px] bg-gradient-to-br from-[rgba(39,197,127,0.16)] to-[rgba(86,185,136,0.10)] border border-[rgba(17,106,71,0.10)] text-4xl">
      <CreditCard size={36} className="text-[#19a56d]" />
    </div>
    <h3 className="m-0 text-[28px] text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.03em] font-extrabold">{t(`${CC}.noCardConnected`)}</h3>
    <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[15px] leading-[1.7] max-w-[760px] mx-auto mt-3 mb-0">
      {t(`${CP}.emptyStateBody`)}
    </p>
    
    <div className="flex justify-center gap-3 flex-wrap mt-[22px]">
      <Button 
        onClick={onAddCard}
        className="gap-2 bg-gradient-to-br from-[#11875a] to-[#27c57f] text-white font-bold shadow-[0_12px_30px_rgba(17,135,90,0.22)]"
        data-testid="add-card-payment-planner-btn"
      >
        <CreditCard size={16} />
        {t(`${CP}.emptyStateAddCard`)}
      </Button>
      <Button 
        onClick={onQuickAdd}
        variant="outline" 
        className="gap-2 border-[rgba(10,95,62,0.16)] opacity-70"
        title={t('common.comingSoon')}
      >
        <Zap size={16} />
        {t(`${CP}.emptyStateQuickAdd`)}
      </Button>
    </div>
    
    <div className="mt-[14px] text-xs text-[#7c9888] dark:text-[#93AE9F] leading-[1.6]">
      {t(`${CP}.emptyStateAfterSave`)}
    </div>
  </div>
  );
};

// ===================== ACCOUNT PLANNER CARD =====================
const AccountPlannerCard = ({ account, onLogEvent, onUpdateCLI, isSelected, onSelect }) => {
  const { t } = useI18n();
  const CP = 'creditBuilder.cliPlanner';
  const AC = `${CP}.accountCard`;
  const SL = `${CP}.strategyLabels`;
  const [activeTab, setActiveTab] = useState('overview');
  const [isSaving, setIsSaving] = useState(false);
  
  // CLI Profile form state
  const [cliForm, setCLIForm] = useState({
    last_cli_request_date: '',
    last_denied_cli_date: '',
    income_on_file: '',
    actual_current_income: '',
    income_last_updated_date: '',
    automatic_increase_history: 'Unknown',
    prior_request_method: 'App',
    pull_type_history: 'Unknown',
    plan_horizon: '90',  // Renamed from target_timeline
    risk_style: 'Balanced',
    capacity_level: 'Medium',
    cli_notes: '',
  });
  
  // Get bucket key for this account
  const accountBucketKey = useMemo(() => 
    getBucketKeyForIssuer(account?.issuer_name, ISSUER_BUCKET_DATA),
    [account?.issuer_name]
  );
  
  // Check if horizon is shorter than bucket
  const horizonMismatch = useMemo(() => 
    isHorizonShorterThanBucket(cliForm.plan_horizon, accountBucketKey),
    [cliForm.plan_horizon, accountBucketKey]
  );
  
  // Get effective horizon days
  const effectiveHorizonDays = useMemo(() => 
    getHorizonDays(cliForm.plan_horizon, accountBucketKey),
    [cliForm.plan_horizon, accountBucketKey]
  );
  
  // Determine if we need extended planning (> 90 days)
  const needsExtendedPlan = effectiveHorizonDays > 90;
  
  // Initialize form with existing cli_fields when account changes
  useEffect(() => {
    const bucketKey = getBucketKeyForIssuer(account?.issuer_name, ISSUER_BUCKET_DATA);
    const defaultHorizon = getDefaultPlanHorizon(bucketKey);
    
    if (account?.cli_fields) {
      const cf = account.cli_fields;
      setCLIForm({
        last_cli_request_date: cf.last_cli_request_date?.split('T')[0] || '',
        last_denied_cli_date: cf.last_denied_cli_date?.split('T')[0] || '',
        income_on_file: cf.income_on_file || '',
        actual_current_income: cf.actual_current_income || '',
        income_last_updated_date: cf.income_last_updated_date?.split('T')[0] || '',
        automatic_increase_history: cf.automatic_increase_history || 'Unknown',
        prior_request_method: cf.prior_request_method || 'App',
        pull_type_history: cf.pull_type_history || account.issuer_rule?.pull_type || 'Unknown',
        plan_horizon: cf.plan_horizon || cf.target_timeline?.replace(' Days', '') || defaultHorizon,
        risk_style: cf.risk_style || 'Balanced',
        capacity_level: cf.capacity_level || 'Medium',
        cli_notes: cf.cli_notes || '',
      });
    } else {
      // Set defaults for new account
      setCLIForm(prev => ({
        ...prev,
        plan_horizon: defaultHorizon,
      }));
    }
  }, [account?.card_id, account?.cli_fields, account?.issuer_name]);
  
  const handleCLIFormChange = (field, value) => {
    setCLIForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSaveCLIProfile = async () => {
    setIsSaving(true);
    try {
      await onUpdateCLI(account.card_id, cliForm);
    } finally {
      setIsSaving(false);
    }
  };
  
  const tabs = useMemo(() => [
    { id: 'overview', label: t(`${AC}.tabs.overview`) },
    { id: 'synced', label: t(`${AC}.tabs.syncedFields`) },
    { id: 'cli', label: t(`${AC}.tabs.completeCliProfile`) },
    { id: 'intel', label: t(`${AC}.tabs.issuerIntelligence`) },
    { id: 'buckets', label: t(`${AC}.tabs.plannerBuckets`) },
    { id: 'strategy', label: t(`${AC}.tabs.targetLimitStrategy`) },
    { id: 'heavy', label: t(`${AC}.tabs.heavyUsageMode`) },
    { id: 'plan', label: needsExtendedPlan ? t(`${AC}.tabs.planExtended`) : t(`${AC}.tabs.planStandard`) },
    { id: 'script', label: t(`${AC}.tabs.callScript`) },
    { id: 'timeline', label: t(`${AC}.tabs.timeline`) },
  ], [t, needsExtendedPlan]);
  
  const planHorizonOptions = useMemo(() => buildPlanHorizonOptions(t), [t]);
  const yesNoUnknownOptions = useMemo(() => buildYesNoUnknownOptions(t, AC), [t]);
  const methodOptions = useMemo(() => buildMethodOptions(t, AC), [t]);
  const pullHistoryOptions = useMemo(() => buildPullOptions(t, AC), [t]);
  const riskOptions = useMemo(() => buildRiskOptions(t, AC), [t]);
  const capacityOptions = useMemo(() => buildCapacityOptions(t, AC), [t]);
  
  const { readiness, issuer_rule, cli_fields } = account;
  
  // Calculate ask range based on current limit and readiness
  const currentLimit = account.limit || 0;
  const conservativeAsk = Math.round(currentLimit * 1.3 / 500) * 500;
  const balancedAsk = Math.round(currentLimit * 1.45 / 500) * 500;
  const stretchAsk = Math.round(currentLimit * 1.6 / 500) * 500;
  
  // Derive status label (known enums only; API label as fallback)
  const statusLabel = translateReadinessStatus(t, readiness?.status, readiness?.status_info?.label || t(`${SL}.unknown`));
  const isReady = readiness?.status === 'ready_now';
  const daysUntilEligible = readiness?.wait_days || 0;
  
  // Simulated heavy usage data (would come from Payment Planner sync)
  const peakCycleUtil = 63;
  const reportedUtil = account.utilization || 0;
  const paydownProgress = 92;
  
  return (
    <article className="p-[18px] rounded-[26px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(17,106,71,0.09)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_16px_38px_rgba(11,79,53,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.30)]">
      {/* Header */}
      <div className="flex flex-col lg:grid lg:grid-cols-[auto_1fr_auto] gap-4 lg:gap-[18px] items-start lg:items-center mb-[18px]">
        <ScoreRing score={readiness?.score || 0} label={isReady ? t(`${CP}.scoreRing.ready`) : t(`${CP}.scoreRing.score`)} />
        
        <div className="min-w-0 w-full">
          <h4 className="m-0 mb-2 text-xl lg:text-2xl text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em] font-extrabold break-words">{account.account_name}</h4>
          <div className="flex flex-wrap gap-2 mb-[10px]">
            <SoftBadge variant={isReady ? 'success' : readiness?.status === 'almost_ready' ? 'gold' : 'amber'}>{statusLabel}</SoftBadge>
            {issuer_rule?.pull_type && (
              <SoftBadge variant={issuer_rule.pull_type === 'hard' ? 'danger' : 'gold'}>
                {t(`${CP}.pullPattern`, {
                  type: issuer_rule.pull_type.charAt(0).toUpperCase() + issuer_rule.pull_type.slice(1),
                })}
              </SoftBadge>
            )}
            <SoftBadge variant="outline">
              {t(`${CP}.confidenceBadge`, { label: issuer_rule?.confidence_info?.label || t('creditBuilder.utilizationPlanner.unknown') })}
            </SoftBadge>
            <SoftBadge variant="success">{t(`${CP}.syncedFromPaymentPlannerBadge`)}</SoftBadge>
          </div>
          <p className="text-[#5b7666] dark:text-[#C7DDD0] text-sm leading-[1.65] m-0 max-w-[820px]">
            {readiness?.best_action || t(`${CP}.header.completeProfileHint`)}
          </p>
        </div>
        
        <div className="w-full lg:w-auto lg:text-right lg:min-w-[220px] mt-2 lg:mt-0">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-[6px]">{t(`${CP}.currentLimit`)}</div>
          <div className="text-2xl lg:text-[30px] font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.03em]">{formatCurrency(currentLimit)}</div>
          <div className="text-xs text-[#5b7666] dark:text-[#C7DDD0] mt-1 leading-[1.5]">
            {t(`${CP}.header.lastCli`)} {cli_fields?.last_cli_request_date ? formatShortDate(cli_fields.last_cli_request_date) : t(`${CP}.header.never`)}<br />
            {t(`${CP}.header.suggestedAskRange`)} {formatCurrency(balancedAsk - 2500)} – {formatCurrency(balancedAsk)}
          </div>
        </div>
      </div>
      
      {/* Quick Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <div className="p-[14px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)]">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-2 font-semibold">{t(`${CP}.daysUntilEligibleShort`)}</div>
          <div className="text-xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">{daysUntilEligible}</div>
          <div className="mt-[6px] text-xs text-[#5b7666] dark:text-[#C7DDD0] leading-[1.45]">
            {daysUntilEligible === 0 ? t(`${CP}.header.readyNow`) : t(`${CP}.header.waitDays`, { count: daysUntilEligible })}
          </div>
        </div>
        <div className="p-[14px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)]">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-2 font-semibold">{t(`${CP}.reportedUtilization`)}</div>
          <div className="text-xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">{formatPercent(reportedUtil)}</div>
          <div className="mt-[6px] text-xs text-[#5b7666] dark:text-[#C7DDD0] leading-[1.45]">{t(`${CP}.header.syncedReportedBalance`)}</div>
        </div>
        <div className="p-[14px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)]">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-2 font-semibold">{t(`${CP}.peakCycleUtilization`)}</div>
          <div className="text-xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">{formatPercent(peakCycleUtil)}</div>
          <div className="mt-[6px] text-xs text-[#5b7666] dark:text-[#C7DDD0] leading-[1.45]">{t(`${CP}.header.heavyUsageTracked`)}</div>
        </div>
        <div className="p-[14px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)]">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-2 font-semibold">{t(`${CP}.incomeOnFile`)}</div>
          <div className="text-xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">{cli_fields?.income_on_file ? formatCurrency(cli_fields.income_on_file) : '—'}</div>
          <div className="mt-[6px] text-xs text-[#5b7666] dark:text-[#C7DDD0] leading-[1.45]">{cli_fields?.income_on_file ? t(`${CP}.header.needsUpdateToCurrent`) : t(`${CP}.header.notSet`)}</div>
        </div>
        <div className="p-[14px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)]">
          <div className="text-xs text-[#7c9888] dark:text-[#93AE9F] mb-2 font-semibold">{t(`${CP}.actualCurrentIncome`)}</div>
          <div className="text-xl font-extrabold text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em]">—</div>
          <div className="mt-[6px] text-xs text-[#5b7666] dark:text-[#C7DDD0] leading-[1.45]">{t(`${CP}.header.supportsStrongerAsk`)}</div>
        </div>
      </div>
      
      {/* Tabs */}
      <PlannerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Tab Panels */}
      <div className="mt-4">
        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px]">
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${CP}.requestReadiness`)}</h5>
              <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                {readiness?.reasons?.length > 0 ? readiness.reasons[0] : t(`${CP}.header.reviewAccountHint`)}
              </p>
              
              <div className="grid gap-[10px] mt-[14px]">
                {readiness?.reasons?.map((reason, i) => (
                  <SignalRow key={i} variant="success" label={t(`${AC}.signals.positive`)}>{reason}</SignalRow>
                ))}
                {readiness?.warnings?.map((warning, i) => (
                  <SignalRow key={i} variant="warning" label={t(`${AC}.signals.monitor`)}>{warning}</SignalRow>
                ))}
                {readiness?.blockers?.map((blocker, i) => (
                  <SignalRow key={i} variant="danger" label={t(`${AC}.signals.blocker`)}>{blocker}</SignalRow>
                ))}
              </div>
              
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
                  <thead>
                    <tr>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.rule`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.status`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.impact`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.rules.waitingPeriod`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]"><SoftBadge variant={daysUntilEligible === 0 ? 'success' : 'amber'}>{daysUntilEligible === 0 ? translateEnum(t, SL, 'Passed') : translateEnum(t, SL, 'Active')}</SoftBadge></td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{daysUntilEligible === 0 ? translateEnum(t, SL, 'Positive') : translateEnum(t, SL, 'Blocking')}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.rules.reportedBalanceTiming`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]"><SoftBadge variant={reportedUtil < 30 ? 'success' : 'amber'}>{reportedUtil < 30 ? translateEnum(t, SL, 'Passed') : translateEnum(t, SL, 'High')}</SoftBadge></td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{reportedUtil < 30 ? translateEnum(t, SL, 'Strong') : translateEnum(t, SL, 'Needs attention')}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px]">{t(`${AC}.rules.askSizeRealism`)}</td>
                      <td className="p-[14px_16px]"><SoftBadge variant="gold">{translateEnum(t, SL, 'Balanced Ask')}</SoftBadge></td>
                      <td className="p-[14px_16px]">{translateEnum(t, SL, 'Preferred')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${CP}.bestNextActionPanel`)}</h5>
              <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                {readiness?.best_action || t(`${CP}.header.completeCliProfileHint`)}
              </p>
              
              <div className="grid gap-[14px] mt-[14px]">
                <ListRow title={t(`${AC}.overview.recommendedAskBand`)} badge={formatCurrency(balancedAsk)} badgeVariant="gold">
                  {t(`${AC}.overview.recommendedAskBandBody`)}
                </ListRow>
                <ListRow title={t(`${AC}.overview.preferredRequestWindow`)} badge={daysUntilEligible === 0 ? translateEnum(t, SL, 'Today') : `${daysUntilEligible} ${t(`${AC}.fields.daysUnit`)}`} badgeVariant={daysUntilEligible === 0 ? 'success' : 'amber'}>
                  {t(`${AC}.overview.preferredRequestWindowBody`)}
                </ListRow>
                <ListRow title={t(`${AC}.overview.confidenceLevel`)} badge={issuer_rule?.confidence_info?.label || t(`${SL}.unknown`)} badgeVariant="neutral">
                  {t(`${AC}.overview.confidenceLevelBody`)}
                </ListRow>
              </div>
            </div>
          </div>
        )}
        
        {/* SYNCED FIELDS */}
        {activeTab === 'synced' && (
          <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
            <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${CP}.syncedFieldsFromPaymentPlanner`)}</h5>
            <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
              {t(`${CP}.syncedFieldsBody`)}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-[14px]">
              <FieldInput label={t(`${AC}.fields.issuer`)} readonly value={account.issuer_name || t(`${SL}.unknown`)} />
              <FieldInput label={t(`${AC}.fields.cardName`)} readonly value={account.account_name || t(`${SL}.unknown`)} />
              <FieldInput label={t(`${AC}.fields.currentLimit`)} readonly value={formatCurrency(account.limit)} />
              <FieldInput label={t(`${AC}.fields.currentBalance`)} readonly value={formatCurrency(account.balance)} />
              <FieldInput label={t(`${AC}.fields.reportedUtilizationEstimate`)} readonly value={formatPercent(account.utilization)} />
              <FieldInput label={t(`${AC}.fields.statementClose`)} readonly value="12th" />
              <FieldInput label={t(`${AC}.fields.dueDate`)} readonly value="8th" />
              <FieldInput label={t(`${AC}.fields.paydownProgress`)} readonly value={`${paydownProgress}%`} />
              <FieldInput label={t(`${AC}.fields.peakUsageTrend`)} readonly value={t(`${AC}.fields.cyclePeak`, { percent: peakCycleUtil })} />
            </div>
          </div>
        )}
        
        {/* COMPLETE CLI PROFILE */}
        {activeTab === 'cli' && (
          <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h5 className="m-0 mb-1 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.tabs.completeCliProfile`)}</h5>
                <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                  {t(`${AC}.fields.completeCliProfileOnlyMissing`)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <SoftBadge variant={PLANNER_BUCKETS[accountBucketKey]?.color || 'neutral'}>
                  {t(`${AC}.fields.bucketLabel`, { label: translatePlannerBucket(t, accountBucketKey, PLANNER_BUCKETS[accountBucketKey]?.label) })}
                </SoftBadge>
              </div>
            </div>
            
            {/* Horizon Mismatch Warning */}
            {horizonMismatch && (
              <div className="p-[14px_16px] rounded-[16px] bg-gradient-to-r from-[#fff8ea] to-[#fffdf8] dark:from-[rgba(245,158,11,0.15)] dark:to-[rgba(245,158,11,0.08)] border border-[rgba(183,121,31,0.12)] dark:border-[rgba(245,158,11,0.20)] flex items-start gap-3 mb-4">
                <AlertTriangle size={18} className="text-[#b7791f] dark:text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[13px] font-bold text-[#b7791f] dark:text-amber-300 mb-1">{t(`${AC}.fields.planHorizonShorterTitle`)}</div>
                  <div className="text-[12px] text-[#8a5a12] dark:text-amber-200 leading-[1.6]">
                    {t(`${AC}.fields.planHorizonShorterBody`, {
                      days: cliForm.plan_horizon,
                      bucket: translatePlannerBucket(t, accountBucketKey, PLANNER_BUCKETS[accountBucketKey]?.label),
                    })}
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-[14px]">
              <FieldInput label={t(`${AC}.fields.lastCliDate`)} type="date" value={cliForm.last_cli_request_date} onChange={(e) => handleCLIFormChange('last_cli_request_date', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.lastDeniedCliDate`)} type="date" value={cliForm.last_denied_cli_date} onChange={(e) => handleCLIFormChange('last_denied_cli_date', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.incomeOnFile`)} value={cliForm.income_on_file} placeholder={t(`${AC}.fields.notEntered`)} onChange={(e) => handleCLIFormChange('income_on_file', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.actualCurrentIncome`)} value={cliForm.actual_current_income} placeholder={t(`${AC}.fields.notEntered`)} onChange={(e) => handleCLIFormChange('actual_current_income', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.incomeLastUpdated`)} type="date" value={cliForm.income_last_updated_date} onChange={(e) => handleCLIFormChange('income_last_updated_date', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.automaticIncreaseHistory`)} options={yesNoUnknownOptions} value={cliForm.automatic_increase_history} onChange={(e) => handleCLIFormChange('automatic_increase_history', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.priorRequestMethod`)} options={methodOptions} value={cliForm.prior_request_method} onChange={(e) => handleCLIFormChange('prior_request_method', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.pullTypeHistory`)} options={pullHistoryOptions} value={cliForm.pull_type_history} onChange={(e) => handleCLIFormChange('pull_type_history', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.planHorizon`)} options={planHorizonOptions.map((opt) => ({ value: opt.value, label: opt.label }))} value={cliForm.plan_horizon} onChange={(e) => handleCLIFormChange('plan_horizon', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.riskStyle`)} options={riskOptions} value={cliForm.risk_style} onChange={(e) => handleCLIFormChange('risk_style', e.target.value)} />
              <FieldInput label={t(`${AC}.fields.capacityLevel`)} options={capacityOptions} value={cliForm.capacity_level} onChange={(e) => handleCLIFormChange('capacity_level', e.target.value)} />
              <FieldInput full label={t(`${AC}.fields.notesFromLastContact`)} type="textarea" value={cliForm.cli_notes} placeholder={t(`${AC}.fields.notesPlaceholder`)} onChange={(e) => handleCLIFormChange('cli_notes', e.target.value)} />
            </div>
            
            {/* Planning Context Info */}
            <div className="p-[14px_16px] rounded-[16px] bg-[#f4fbf7] border border-[rgba(10,95,62,0.10)] mt-4 flex items-start gap-3">
              <Info size={16} className="text-[#0f6a47] shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">
                <strong>Plan Horizon:</strong> {effectiveHorizonDays} days • 
                <strong> Phase Coverage:</strong> {needsExtendedPlan ? 'Extended (first 90 days detailed, later phases milestone-based)' : 'Full coverage in 30/60/90 view'} • 
                <strong> Bucket:</strong> {PLANNER_BUCKETS[accountBucketKey]?.label}
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <Button 
                onClick={handleSaveCLIProfile} 
                disabled={isSaving}
                className="gap-2 bg-gradient-to-br from-[#11875a] to-[#27c57f] text-white font-bold"
                data-testid="save-cli-profile-btn"
              >
                <Edit3 size={14} />
                {isSaving ? t(`${AC}.fields.saving`) : t(`${AC}.fields.saveCliProfile`)}
              </Button>
            </div>
          </div>
        )}
        
        {/* ISSUER INTELLIGENCE */}
        {activeTab === 'intel' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px]">
            <div className="p-4 lg:p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.tabs.issuerIntelligence`)}</h5>
              <div className="grid gap-[14px] mt-[14px]">
                <ListRow title={t(`${AC}.intel.requestChannel`)} badge={translateEnum(t, SL, 'Verified')} badgeVariant="success">{t(`${AC}.intel.requestChannelValue`)}</ListRow>
                <ListRow title={t(`${AC}.intel.pullPattern`)} badge={issuer_rule?.pull_type === 'hard' ? t(`${AC}.script.pullHard`) : `${t(`${AC}.intel.likely`)} ${t(`${AC}.script.pullSoft`)}`} badgeVariant={issuer_rule?.pull_type === 'hard' ? 'danger' : 'gold'}>
                  {issuer_rule?.pull_type === 'hard' ? t(`${AC}.intel.hardPullCli`) : t(`${AC}.intel.softMixedProfile`)}
                </ListRow>
                <ListRow title={t(`${AC}.intel.automaticIncreaseTendency`)} badge={t(`${AC}.options.yes`)} badgeVariant="success">{t(`${AC}.intel.periodicReview`)}</ListRow>
                <ListRow title={t(`${AC}.intel.cooldownPattern`)} badge={VERIFICATION_LEVELS.community.label} badgeVariant="gold">
                  {t(`${AC}.intel.cooldownPatternValue`, { days: issuer_rule?.min_days_since_last_request || 91 })}
                </ListRow>
                <ListRow title={t(`${AC}.intel.customAmountAllowed`)} badge={t(`${AC}.intel.likely`)} badgeVariant="gold">{t(`${AC}.intel.likelyVaries`)}</ListRow>
              </div>
            </div>
            
            <div className="p-4 lg:p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.intel.verificationStatus`)}</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-0">
                <FieldInput label={t(`${AC}.intel.verificationStatus`)} value={t(`${AC}.intel.patternSuffix`, { label: issuer_rule?.confidence_info?.label || t(`${SL}.unknown`) })} readonly />
                <FieldInput label={t(`${AC}.intel.confidenceLevel`)} value={issuer_rule?.confidence_info?.label || t(`${SL}.unknown`)} readonly />
                <FieldInput label={t(`${AC}.intel.lastVerified`)} value={formatDate(new Date().toISOString())} readonly />
                <FieldInput label={t(`${AC}.intel.sourceType`)} value={issuer_rule?.confidence_source || t(`${SL}.unknown`)} readonly />
                <FieldInput label={t(`${AC}.intel.plannerBucket`)} value={ISSUER_BUCKET_DATA.find(i => account.issuer_name?.toLowerCase().includes(i.issuer.toLowerCase().split(' ')[0]))?.uiLabel || t(`${AC}.buckets.manualReview`)} readonly />
                <FieldInput label={t(`${AC}.intel.timingConfidence`)} value={VERIFICATION_LEVELS[ISSUER_BUCKET_DATA.find(i => account.issuer_name?.toLowerCase().includes(i.issuer.toLowerCase().split(' ')[0]))?.verificationStatus || 'needs_reconfirmation']?.label || t(`${AC}.buckets.manualReview`)} readonly />
                <FieldInput full label={t(`${AC}.intel.exceptionNote`)} type="textarea" readonly value={issuer_rule?.notes || t(`${AC}.intel.exceptionNoteDefault`)} />
              </div>
            </div>
          </div>
        )}
        
        {/* PLANNER BUCKET INTELLIGENCE */}
        {activeTab === 'buckets' && (
          <div className="space-y-[18px]">
            {/* Intro Section */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-4 lg:gap-[18px]">
              <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-[rgba(39,197,127,0.12)] to-[rgba(86,185,136,0.08)]">
                    <BarChart3 size={18} className="text-[#19a56d]" />
                  </div>
                  <h5 className="m-0 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.buckets.title`)}</h5>
                </div>
                <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.7] m-0 mb-4">
                  {t(`${AC}.buckets.intro1`)}
                </p>
                <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.7] m-0">
                  {t(`${AC}.buckets.intro2`)}
                </p>
              </div>
              
              {/* Trust Legend */}
              <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <h5 className="m-0 mb-3 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.buckets.trustLegend`)}</h5>
                <div className="grid gap-[10px]">
                  <div className="flex items-center gap-3 p-[10px_12px] rounded-[12px] bg-[#e8f8ef] border border-[rgba(17,106,71,0.08)]">
                    <CheckCircle size={16} className="text-[#0f6a47]" />
                    <div>
                      <div className="text-[13px] font-bold text-[#0f6a47]">{VERIFICATION_LEVELS.official.label}</div>
                      <div className="text-[11px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.buckets.officiallyVerifiedDesc`)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-[10px_12px] rounded-[12px] bg-[#fdf6e8] border border-[rgba(178,138,52,0.08)]">
                    <Shield size={16} className="text-[#b28a34]" />
                    <div>
                      <div className="text-[13px] font-bold text-[#b28a34] dark:text-amber-300">{VERIFICATION_LEVELS.likely.label}</div>
                      <div className="text-[11px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.buckets.likelyEvidenceDesc`)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-[10px_12px] rounded-[12px] bg-[#fff8ea] dark:bg-[rgba(245,158,11,0.12)] border border-[rgba(183,121,31,0.08)] dark:border-[rgba(245,158,11,0.18)]">
                    <Info size={16} className="text-[#b7791f] dark:text-amber-300" />
                    <div>
                      <div className="text-[13px] font-bold text-[#b7791f] dark:text-amber-300">{VERIFICATION_LEVELS.community.label}</div>
                      <div className="text-[11px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.buckets.communityDesc`)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-[10px_12px] rounded-[12px] bg-[#f4f7f5] dark:bg-[rgba(84,106,93,0.12)] border border-[rgba(84,106,93,0.08)] dark:border-[rgba(142,218,182,0.12)]">
                    <HelpCircle size={16} className="text-[#546a5d] dark:text-[#93AE9F]" />
                    <div>
                      <div className="text-[13px] font-bold text-[#546a5d] dark:text-[#C7DDD0]">{VERIFICATION_LEVELS.needs_reconfirmation.label}</div>
                      <div className="text-[11px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.buckets.needsReconfirmationDesc`)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Planner Buckets Visual */}
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-3 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.buckets.plannerBucketsTitle`)}</h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
                {Object.entries(PLANNER_BUCKETS).map(([key, bucket]) => (
                  <div key={key} className={`p-[14px] rounded-[16px] border ${
                    bucket.color === 'success' ? 'bg-[#e8f8ef] dark:bg-[rgba(16,185,129,0.12)] border-[rgba(17,106,71,0.12)] dark:border-[rgba(16,185,129,0.20)]' :
                    bucket.color === 'gold' ? 'bg-[#fdf6e8] dark:bg-[rgba(178,138,52,0.12)] border-[rgba(178,138,52,0.12)] dark:border-[rgba(245,158,11,0.20)]' :
                    bucket.color === 'amber' ? 'bg-[#fff8ea] dark:bg-[rgba(245,158,11,0.12)] border-[rgba(183,121,31,0.12)] dark:border-[rgba(245,158,11,0.20)]' :
                    'bg-[#f4f7f5] dark:bg-[rgba(84,106,93,0.12)] border-[rgba(84,106,93,0.12)] dark:border-[rgba(142,218,182,0.12)]'
                  }`}>
                    <div className={`text-sm font-bold mb-1 ${
                      bucket.color === 'success' ? 'text-[#0f6a47] dark:text-emerald-300' :
                      bucket.color === 'gold' ? 'text-[#b28a34] dark:text-amber-300' :
                      bucket.color === 'amber' ? 'text-[#b7791f] dark:text-amber-300' :
                      'text-[#546a5d] dark:text-[#93AE9F]'
                    }`}>{translatePlannerBucket(t, key, bucket.label)}</div>
                    <div className="text-[11px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.4]">{translatePlannerBucketDesc(t, key, bucket.description)}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Caution Note */}
            <div className="p-[14px_16px] rounded-[16px] bg-gradient-to-r from-[#fff8ea] to-[#fffdf8] dark:from-[rgba(245,158,11,0.15)] dark:to-[rgba(245,158,11,0.08)] border border-[rgba(183,121,31,0.12)] dark:border-[rgba(245,158,11,0.20)] flex items-start gap-3">
              <AlertTriangle size={18} className="text-[#b7791f] dark:text-amber-300 shrink-0 mt-0.5" />
              <div>
                <div className="text-[13px] font-bold text-[#b7791f] dark:text-amber-300 mb-1">{t(`${AC}.buckets.planningFrameworkTitle`)}</div>
                <div className="text-[12px] text-[#8a5a12] dark:text-amber-200 leading-[1.6]">
                  {t(`${AC}.buckets.planningFrameworkBody`)}
                </div>
              </div>
            </div>
            
            {/* Issuer Bucket Matrix */}
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-3 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.buckets.issuerBucketMatrix`)}</h5>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
                  <thead>
                    <tr>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.issuer`)}</th>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.plannerBucketCol`)}</th>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.publicWaitingRule`)}</th>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.pullConfidence`)}</th>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.autoIncrease`)}</th>
                      <th className="p-[12px_14px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.verification`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ISSUER_BUCKET_DATA.map((issuer, idx) => {
                      const bucket = PLANNER_BUCKETS[issuer.bucket];
                      const verification = VERIFICATION_LEVELS[issuer.verificationStatus];
                      return (
                        <tr key={idx} className="dark:text-[#C7DDD0]">
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                            <div className="font-semibold text-[#0b4f35] dark:text-[#EAF7F0]">{issuer.issuer}</div>
                            {issuer.notes && <div className="text-[11px] text-[#7c9888] dark:text-[#93AE9F] mt-1 leading-[1.4]">{issuer.notes}</div>}
                          </td>
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                            <SoftBadge variant={bucket?.color || 'neutral'}>{translatePlannerBucket(t, issuer.bucket, bucket?.label || t(`${SL}.unknown`))}</SoftBadge>
                          </td>
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)] text-[#5b7666] dark:text-[#C7DDD0]">{issuer.publicRule}</td>
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                            <SoftBadge variant={issuer.pullConfidence.level === 'official' ? 'success' : issuer.pullConfidence.level === 'likely' ? 'gold' : issuer.pullConfidence.level === 'community' ? 'amber' : 'neutral'}>
                              {issuer.pullConfidence.text}
                            </SoftBadge>
                          </td>
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                            <SoftBadge variant={issuer.autoIncreaseConfidence.level === 'official' ? 'success' : issuer.autoIncreaseConfidence.level === 'likely' ? 'gold' : issuer.autoIncreaseConfidence.level === 'community' ? 'amber' : 'neutral'}>
                              {issuer.autoIncreaseConfidence.text}
                            </SoftBadge>
                          </td>
                          <td className="p-[12px_14px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                            <SoftBadge variant={verification?.color || 'neutral'}>{verification?.label || t(`${SL}.unknown`)}</SoftBadge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Current Card Bucket */}
            <div className="p-[18px] rounded-[22px] bg-gradient-to-br from-[#e8f8ef] to-[#f4fbf7] border border-[rgba(17,106,71,0.12)] shadow-[0_8px_20px_rgba(11,79,53,0.05)]">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h5 className="m-0 mb-1 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">
                    {t(`${AC}.buckets.cardPlannerBucketTitle`, { name: account.account_name })}
                  </h5>
                  <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] m-0">
                    {t(`${AC}.buckets.basedOnIssuer`)} <strong>{account.issuer_name || t(`${SL}.unknown`)}</strong>
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  {(() => {
                    const issuerData = ISSUER_BUCKET_DATA.find(i => 
                      account.issuer_name?.toLowerCase().includes(i.issuer.toLowerCase().split(' ')[0])
                    ) || ISSUER_BUCKET_DATA[ISSUER_BUCKET_DATA.length - 1];
                    const bucket = PLANNER_BUCKETS[issuerData.bucket];
                    const verification = VERIFICATION_LEVELS[issuerData.verificationStatus];
                    return (
                      <>
                        <SoftBadge variant={bucket?.color || 'neutral'} className="text-sm px-4 py-2">
                          {translatePlannerBucket(t, issuerData.bucket, bucket?.label || t(`${AC}.buckets.manualReview`))}
                        </SoftBadge>
                        <SoftBadge variant={verification?.color || 'neutral'}>
                          {verification?.label || t(`${AC}.buckets.manualReview`)}
                        </SoftBadge>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* TARGET LIMIT STRATEGY */}
        {activeTab === 'strategy' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px]">
            <div className="p-4 lg:p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.tabs.targetLimitStrategy`)}</h5>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
                  <thead>
                    <tr>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.askBand`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.target`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.risk`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.incomeSupport`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.usageSupport`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.strategy.conservativeAsk`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{formatCurrency(conservativeAsk)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]"><SoftBadge variant="success">{translateEnum(t, SL, 'Low')}</SoftBadge></td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{translateEnum(t, SL, 'Strong')}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{translateEnum(t, SL, 'Strong')}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.strategy.balancedAsk`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{formatCurrency(balancedAsk)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]"><SoftBadge variant="gold">{translateEnum(t, SL, 'Moderate')}</SoftBadge></td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{translateEnum(t, SL, 'Strong')}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{translateEnum(t, SL, 'Strong')}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px]">{t(`${AC}.strategy.stretchAsk`)}</td>
                      <td className="p-[14px_16px]">{formatCurrency(stretchAsk)}</td>
                      <td className="p-[14px_16px]"><SoftBadge variant="danger">{translateEnum(t, SL, 'Higher')}</SoftBadge></td>
                      <td className="p-[14px_16px]">{translateEnum(t, SL, 'Good')}</td>
                      <td className="p-[14px_16px]">{translateEnum(t, SL, 'Good')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.strategy.askFraming`)}</h5>
              <div className="grid gap-[14px] mt-[14px]">
                <ListRow title={t(`${AC}.strategy.askFormat`)} badge={`${formatCurrency(currentLimit)} → ${formatCurrency(balancedAsk)}`} badgeVariant="neutral">
                  {t(`${AC}.strategy.askFormatBody`)}
                </ListRow>
                <ListRow title={t(`${AC}.strategy.currentAiRecommendation`)} badge={translateEnum(t, SL, 'Balanced')} badgeVariant="gold">
                  {t(`${AC}.strategy.currentAiRecommendationBody`)}
                </ListRow>
                <ListRow title={t(`${AC}.strategy.redFlagGuardrail`)} badge={t(`${CP}.cockpitStats.trendWatch`)} badgeVariant="amber">
                  {t(`${AC}.strategy.redFlagGuardrailBody`)}
                </ListRow>
              </div>
            </div>
          </div>
        )}
        
        {/* HEAVY USAGE MODE */}
        {activeTab === 'heavy' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px]">
            <div className="p-4 lg:p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.heavy.title`)}</h5>
              <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                {t(`${AC}.heavy.body`)}
              </p>
              
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
                  <thead>
                    <tr>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.metric`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.value`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.status`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.heavy.peakCycleUtilization`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{peakCycleUtil}%</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]"><SoftBadge variant="gold">{t(`${AC}.heavy.highUsage`)}</SoftBadge></td>
                    </tr>
                    <tr>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]">{t(`${AC}.heavy.reportedUtilizationEstimate`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]">{formatPercent(reportedUtil)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]"><SoftBadge variant="success">{t(`${AC}.heavy.controlled`)}</SoftBadge></td>
                    </tr>
                    <tr>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]">{t(`${AC}.heavy.paidDownBeforeStatement`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]">$6,900</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)]"><SoftBadge variant="success">{t(`${AC}.heavy.yes`)}</SoftBadge></td>
                    </tr>
                    <tr>
                      <td className="p-[14px_16px]">{t(`${AC}.heavy.patternStability`)}</td>
                      <td className="p-[14px_16px]">{t(`${AC}.heavy.stable`)}</td>
                      <td className="p-[14px_16px]"><SoftBadge variant="success">{translateEnum(t, SL, 'Good')}</SoftBadge></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="grid gap-[14px]">
              <ProgressCard title={t(`${AC}.heavy.paydownProgress`)} value={`${paydownProgress}%`} percent={paydownProgress} subtitle={t(`${AC}.heavy.paydownProgressSubtitle`)} total="$6,900 / $7,500" />
              <ProgressCard title={t(`${AC}.heavy.behaviorStrength`)} value={translateEnum(t, SL, 'Strong')} percent={81} subtitle={t(`${AC}.heavy.behaviorStrengthSubtitle`)} total="81 / 100" />
              
              <div className="grid gap-[10px]">
                <SignalRow variant="success" label={t(`${AC}.signals.positive`)}>{t(`${AC}.heavy.positiveSpendPaydown`)}</SignalRow>
                <SignalRow variant="warning" label={t(`${AC}.signals.monitor`)}>{t(`${AC}.heavy.warningPaydownTiming`)}</SignalRow>
                <SignalRow variant="danger" label={t(`${AC}.signals.blocker`)}>{t(`${AC}.heavy.guardrailStrain`)}</SignalRow>
              </div>
            </div>
          </div>
        )}
        
        {/* 30/60/90 DAY PLAN - Bucket-Aware */}
        {activeTab === 'plan' && (
          <div className="space-y-[18px]">
            {/* Planning Context Header */}
            <div className="p-[14px_16px] rounded-[16px] bg-gradient-to-r from-[#e8f8ef] to-[#f4fbf7] border border-[rgba(17,106,71,0.10)]">
              <div className="flex items-center justify-between gap-4 mb-2">
                <div className="flex items-center gap-2">
                  <Info size={18} className="text-[#0f6a47]" />
                  <span className="text-[13px] font-bold text-[#0f6a47]">
                    {needsExtendedPlan ? t(`${AC}.plan.extendedFramework`) : t(`${AC}.plan.preparationFramework`)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <SoftBadge variant={PLANNER_BUCKETS[accountBucketKey]?.color || 'neutral'}>
                    {t(`${AC}.plan.bucketPrefix`)} {translatePlannerBucket(t, accountBucketKey, PLANNER_BUCKETS[accountBucketKey]?.label)}
                  </SoftBadge>
                  <SoftBadge variant="gold">
                    {t(`${AC}.plan.horizonPrefix`)} {effectiveHorizonDays} {t(`${AC}.plan.daysUnit`)}
                  </SoftBadge>
                  {needsExtendedPlan && (
                    <SoftBadge variant="amber">{t(`${AC}.plan.phase1Extended`)}</SoftBadge>
                  )}
                </div>
              </div>
              <div className="text-[12px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">
                {needsExtendedPlan
                  ? t(`${AC}.plan.extendedHorizonActive`, { bucket: translatePlannerBucket(t, accountBucketKey, PLANNER_BUCKETS[accountBucketKey]?.label) })
                  : t(`${AC}.plan.standardPlanIntro`)}
              </div>
            </div>
            
            {/* Phase 1: First 30/60/90 Days */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <h5 className="m-0 mb-[14px] text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">
                  {needsExtendedPlan ? t(`${AC}.plan.first30Days`) : t(`${AC}.plan.thirtyDayView`)}
                </h5>
                <div className="grid gap-[14px]">
                  <ListRow title={needsExtendedPlan ? t(`${AC}.plan.phase1Target`) : t(`${AC}.plan.projectedRequestWindow`)}>
                    {needsExtendedPlan ? t(`${AC}.plan.foundationBuilding`) : 'Apr 15, 2026'}
                  </ListRow>
                  <ListRow title={t(`${AC}.plan.weeklySpendTarget`)}>$1,850</ListRow>
                  <ListRow title={t(`${AC}.plan.paydownReminder`)}>{t(`${AC}.plan.daysBeforeStatement`)}</ListRow>
                </div>
              </div>
              
              <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <h5 className="m-0 mb-[14px] text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">
                  {needsExtendedPlan ? t(`${AC}.plan.first60Days`) : t(`${AC}.plan.sixtyDayView`)}
                </h5>
                <div className="grid gap-[14px]">
                  <ListRow title={t(`${AC}.plan.incomeUpdateTask`)}>{t(`${AC}.plan.completeBeforeRequest`)}</ListRow>
                  <ListRow title={t(`${AC}.plan.nextCheckIn`)}>May 8, 2026</ListRow>
                  <ListRow title={t(`${AC}.fields.riskStyle`)}>{translateEnum(t, SL, cliForm.risk_style)}</ListRow>
                </div>
              </div>
              
              <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <h5 className="m-0 mb-[14px] text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">
                  {needsExtendedPlan ? t(`${AC}.plan.first90Days`) : t(`${AC}.plan.ninetyDayView`)}
                </h5>
                <div className="grid gap-[14px]">
                  <ListRow title={t(`${AC}.plan.accountability`)}>{t(`${AC}.plan.trackEachCycle`)}</ListRow>
                  <ListRow title={t(`${AC}.plan.dueDateReminder`)}>{t(`${AC}.plan.daysBeforeDue`)}</ListRow>
                  <ListRow title={t(`${AC}.plan.recommendedAskRange`)}>{formatCurrency(balancedAsk - 500)} – {formatCurrency(balancedAsk)}</ListRow>
                </div>
              </div>
            </div>
            
            {/* Extended Phases (only shown for buckets > 90 days) */}
            {needsExtendedPlan && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[rgba(10,95,62,0.15)] to-transparent" />
                  <span className="text-xs font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em]">{t(`${AC}.plan.extendedPhases`)}</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[rgba(10,95,62,0.15)] to-transparent" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {effectiveHorizonDays >= 180 && (
                    <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-[#fffef8] to-white dark:from-[rgba(183,121,31,0.18)] dark:to-[rgba(183,121,31,0.10)] border border-[rgba(183,121,31,0.12)] dark:border-[rgba(245,158,11,0.25)] shadow-[0_8px_20px_rgba(183,121,31,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="m-0 text-[#8a6914] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.plan.phase90_180`)}</h5>
                        <SoftBadge variant="gold">{t(`${AC}.plan.milestoneBased`)}</SoftBadge>
                      </div>
                      <div className="grid gap-[12px]">
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(183,121,31,0.08)] dark:border-[rgba(183,121,31,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.month4Milestone`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.month4Body`)}</div>
                        </div>
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(183,121,31,0.08)] dark:border-[rgba(183,121,31,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.month5Milestone`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.month5Body`)}</div>
                        </div>
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(183,121,31,0.08)] dark:border-[rgba(183,121,31,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.month6Target`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.month6Body`)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* 181-365 Day Phase */}
                  {effectiveHorizonDays >= 365 && (
                    <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-[#fef8f0] to-white dark:from-[rgba(194,120,62,0.18)] dark:to-[rgba(194,120,62,0.10)] border border-[rgba(194,120,62,0.12)] dark:border-[rgba(194,120,62,0.25)] shadow-[0_8px_20px_rgba(194,120,62,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="m-0 text-[#9a5c1e] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.plan.phase181_365`)}</h5>
                        <SoftBadge variant="amber">{t(`${AC}.plan.longHorizon`)}</SoftBadge>
                      </div>
                      <div className="grid gap-[12px]">
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(194,120,62,0.08)] dark:border-[rgba(194,120,62,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.months7_9`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.months7_9Body`)}</div>
                        </div>
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(194,120,62,0.08)] dark:border-[rgba(194,120,62,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.months10_12`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.months10_12Body`)}</div>
                        </div>
                        <div className="p-3 rounded-[12px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(194,120,62,0.08)] dark:border-[rgba(194,120,62,0.20)]">
                          <div className="text-[11px] font-bold text-[#7c9888] dark:text-[#93AE9F] uppercase tracking-[0.04em] mb-1">{t(`${AC}.plan.month12Window`)}</div>
                          <div className="text-[13px] text-[#5b7666] dark:text-[#C7DDD0]">{t(`${AC}.plan.month12Body`)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Issuer Timing Context */}
            <div className="p-[14px_16px] rounded-[16px] bg-[#f4f7f5] border border-[rgba(84,106,93,0.10)] flex items-start gap-3">
              <Clock size={16} className="text-[#546a5d] shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">
                <strong>{t(`${AC}.plan.currentPlanningPhase`)}</strong> {needsExtendedPlan ? t(`${AC}.plan.first90Detailed`) : t(`${AC}.plan.full909090`)} • 
                <strong> {t(`${AC}.plan.issuerBucketLabel`)}</strong> {translatePlannerBucket(t, accountBucketKey, PLANNER_BUCKETS[accountBucketKey]?.label)} • 
                <strong> {t(`${AC}.plan.earliestRequestWindow`)}</strong> {t(`${AC}.plan.approxDays`, { days: Math.max(readiness?.wait_days || 0, 30) })}
                {needsExtendedPlan && (
                  <> • {t(`${AC}.plan.phaseCoverageExtendedActive`)}</>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* CALL SCRIPT */}
        {activeTab === 'script' && (
          <CLIScriptBox account={account} onSaveOutcome={() => onLogEvent(account)} />
        )}
        
        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
            <h5 className="m-0 mb-[14px] text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${AC}.tabs.timeline`)}</h5>
            <div className="relative mt-3 pl-6 grid gap-[14px]">
              <div className="absolute left-2 top-1 bottom-1 w-[2px] bg-gradient-to-b from-[rgba(17,106,71,0.18)] to-[rgba(17,106,71,0.04)]" />
              
              {cli_fields?.income_last_updated_date && (
                <div className="relative p-[14px_16px] rounded-[16px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                  <div className="absolute w-3 h-3 rounded-full left-[-22px] top-[18px] bg-gradient-to-br from-[#19a56d] to-[#27c57f] shadow-[0_0_0_5px_#eef8f2]" />
                  <span className="inline-block mb-2 text-xs font-bold text-[#7c9888] dark:text-[#93AE9F]">{formatDate(cli_fields.income_last_updated_date)}</span>
                  <h6 className="m-0 mb-[5px] text-sm text-[#0b4f35] dark:text-[#EAF7F0]">{t(`${AC}.timeline.incomeUpdated`)}</h6>
                  <p className="m-0 text-[13px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">{t(`${AC}.timeline.incomeUpdatedBody`)}</p>
                </div>
              )}
              
              {cli_fields?.last_cli_request_date && (
                <div className="relative p-[14px_16px] rounded-[16px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                  <div className="absolute w-3 h-3 rounded-full left-[-22px] top-[18px] bg-gradient-to-br from-[#19a56d] to-[#27c57f] shadow-[0_0_0_5px_#eef8f2]" />
                  <span className="inline-block mb-2 text-xs font-bold text-[#7c9888] dark:text-[#93AE9F]">{formatDate(cli_fields.last_cli_request_date)}</span>
                  <h6 className="m-0 mb-[5px] text-sm text-[#0b4f35] dark:text-[#EAF7F0]">{t(`${AC}.timeline.previousCli`)}</h6>
                  <p className="m-0 text-[13px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">{t(`${AC}.timeline.previousCliBody`)}</p>
                </div>
              )}
              
              <div className="relative p-[14px_16px] rounded-[16px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <div className="absolute w-3 h-3 rounded-full left-[-22px] top-[18px] bg-gradient-to-br from-[#19a56d] to-[#27c57f] shadow-[0_0_0_5px_#eef8f2]" />
                <span className="inline-block mb-2 text-xs font-bold text-[#7c9888] dark:text-[#93AE9F]">{t(`${AC}.timeline.dateRangeSample`)}</span>
                <h6 className="m-0 mb-[5px] text-sm text-[#0b4f35] dark:text-[#EAF7F0]">{t(`${AC}.timeline.highSpendPattern`)}</h6>
                <p className="m-0 text-[13px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">{t(`${AC}.timeline.highSpendPatternBody`)}</p>
              </div>
              
              <div className="relative p-[14px_16px] rounded-[16px] bg-gradient-to-b from-white to-[#f9fcfa] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                <div className="absolute w-3 h-3 rounded-full left-[-22px] top-[18px] bg-gradient-to-br from-[#19a56d] to-[#27c57f] shadow-[0_0_0_5px_#eef8f2]" />
                <span className="inline-block mb-2 text-xs font-bold text-[#7c9888] dark:text-[#93AE9F]">{formatDate(new Date().toISOString())}</span>
                <h6 className="m-0 mb-[5px] text-sm text-[#0b4f35] dark:text-[#EAF7F0]">{t(`${AC}.timeline.recommendedNextRequest`)}</h6>
                <p className="m-0 text-[13px] text-[#5b7666] dark:text-[#C7DDD0] leading-[1.6]">{t(`${AC}.timeline.recommendedNextRequestBody`)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// ===================== WATCHLIST CARD =====================
const WatchlistCard = ({ account }) => {
  const { t } = useI18n();
  const CP = 'creditBuilder.cliPlanner';
  const AC = `${CP}.accountCard`;
  const WL = `${CP}.watchlist`;
  const bucketKey = getBucketKeyForIssuer(account?.issuer_name, ISSUER_BUCKET_DATA);
  const bucket = PLANNER_BUCKETS[bucketKey];
  const waitDays = account.readiness?.wait_days || 0;
  
  const getCurrentPhase = () => {
    if (waitDays <= 30) return t(`${WL}.phaseActivePreparation`);
    if (waitDays <= 90) return t(`${WL}.phaseFirst90`);
    if (waitDays <= 180) return t(`${WL}.phaseExtended90_180`);
    return t(`${WL}.phaseLongHorizon`);
  };
  
  return (
    <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h5 className="m-0 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{account.account_name}</h5>
        <SoftBadge variant={bucket?.color || 'neutral'}>{translatePlannerBucket(t, bucketKey, bucket?.label || t(`${WL}.unknownBucket`))}</SoftBadge>
      </div>
      <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
        {t(`${WL}.body`)}
      </p>
      
      <div className="overflow-x-auto mt-4">
        <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
          <thead>
            <tr>
              <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.metric`)}</th>
              <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${AC}.tables.value`)}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${WL}.plannerBucket`)}</td>
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">
                <SoftBadge variant={bucket?.color || 'neutral'}>{translatePlannerBucket(t, bucketKey, bucket?.label)}</SoftBadge>
              </td>
            </tr>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${WL}.daysUntilEligible`)}</td>
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.plan.approxDays`, { days: waitDays })}</td>
            </tr>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${WL}.currentPlanningPhase`)}</td>
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{getCurrentPhase()}</td>
            </tr>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${WL}.earliestRequestWindow`)}</td>
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${AC}.plan.approxDays`, { days: Math.max(waitDays, 30) })}</td>
            </tr>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${WL}.reportedUtilization`)}</td>
              <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{formatPercent(account.utilization)}</td>
            </tr>
            <tr className="dark:text-[#C7DDD0]">
              <td className="p-[14px_16px]">{t(`${WL}.targetAskAfterPaydown`)}</td>
              <td className="p-[14px_16px]">{formatCurrency(Math.round(account.limit * 1.3 / 500) * 500)} – {formatCurrency(Math.round(account.limit * 1.4 / 500) * 500)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
const CLIPlanner = () => {
  const { t } = useI18n();
  const CC = 'creditBuilder.creditCards';
  const CP = 'creditBuilder.cliPlanner';
  const CS = `${CP}.cockpitStats`;
  const PP = 'creditBuilder.paymentPlanner';
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Derived state
  const selectedAccount = useMemo(() => 
    accounts.find(a => a.card_id === selectedAccountId) || accounts[0],
    [accounts, selectedAccountId]
  );
  
  const watchlistAccounts = useMemo(() => 
    accounts.filter(a => a.card_id !== selectedAccount?.card_id).slice(0, 2),
    [accounts, selectedAccount]
  );
  
  // Fetch accounts with readiness
  const fetchAccounts = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      // Seed rules first
      try {
        await axios.post(`${API}/cli-planner/seed-rules`, {}, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
      } catch {}
      
      const response = await axios.get(`${API}/cli-planner/accounts`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const fetchedAccounts = response.data.accounts || [];
      setAccounts(fetchedAccounts);
      setSummary(response.data.summary || {});
      
      // Check for return from Payment Planner with new card
      const returnCardId = searchParams.get('selectCard');
      const syncedReturn = searchParams.get('synced');
      
      if (returnCardId) {
        const foundCard = fetchedAccounts.find(a => a.card_id === returnCardId);
        if (foundCard) {
          setSelectedAccountId(returnCardId);
          // Show success message if this was a synced return
          if (syncedReturn === 'true') {
            toast.success(t(`${CP}.toasts.cardSynced`, { name: foundCard.account_name }));
          }
        }
        // Clear the URL params after handling
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('selectCard');
        newParams.delete('synced');
        window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
      } else if (!selectedAccountId && fetchedAccounts.length > 0) {
        setSelectedAccountId(fetchedAccounts[0].card_id);
      }
      
      // Update last sync timestamp
      setLastSyncTime(new Date());
      
    } catch (error) {
      console.error('Error fetching CLI accounts:', error);
      toast.error(t(`${CP}.toasts.loadAccountsFailed`));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, searchParams, selectedAccountId]);
  
  useEffect(() => {
    fetchAccounts();
  }, []);
  
  // Navigate to Payment Planner to add card
  const handleAddCardInPaymentPlanner = () => {
    // Navigate to Payment Planner with return flag
    navigate('/credit-builder/payment-planner?returnTo=cli-planner&action=addCard');
  };
  
  // Navigate to Payment Planner overview
  const handleGoToPaymentPlanner = () => {
    // Pass current selected card if available
    if (selectedAccountId) {
      navigate(`/credit-builder/payment-planner?highlightCard=${selectedAccountId}`);
    } else {
      navigate('/credit-builder/payment-planner');
    }
  };
  
  // Refresh sync - re-fetch all data from backend
  const handleRefreshSync = async () => {
    setRefreshing(true);
    try {
      await fetchAccounts(true);
      toast.success(t(`${CP}.toasts.syncRefreshed`));
    } catch (error) {
      toast.error(t(`${CP}.toasts.syncRefreshFailed`));
    }
  };
  
  // Quick Add - Coming soon
  const handleQuickAdd = () => {
    toast.info(t(`${CP}.quickAddToast`));
  };
  
  // Jump to Complete CLI Profile tab
  const handleJumpToCLIProfile = () => {
    // This will be handled by the AccountPlannerCard component
    toast.info(t(`${CP}.toasts.scrollToCliProfile`));
  };
  
  // Log CLI event
  const handleLogEvent = async (account) => {
    // Open the script tab which has the outcome logging
    toast.info(t(`${CP}.toasts.useCallScriptTab`));
  };
  
  // Update CLI fields - API call
  const handleUpdateCLI = async (cardId, formData) => {
    try {
      // Parse income values to numbers if they exist
      const updateData = {
        ...formData,
        income_on_file: formData.income_on_file ? parseFloat(String(formData.income_on_file).replace(/[$,]/g, '')) : null,
        actual_current_income: formData.actual_current_income ? parseFloat(String(formData.actual_current_income).replace(/[$,]/g, '')) : null,
      };
      
      // Remove empty string dates
      if (!updateData.last_cli_request_date) delete updateData.last_cli_request_date;
      if (!updateData.last_denied_cli_date) delete updateData.last_denied_cli_date;
      if (!updateData.income_last_updated_date) delete updateData.income_last_updated_date;
      
      const response = await axios.patch(`${API}/cli-planner/accounts/${cardId}`, updateData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success(t(`${CP}.toasts.cliProfileSaved`));
        // Refresh accounts to show updated data
        await fetchAccounts(true);
      }
    } catch (error) {
      console.error('Error saving CLI profile:', error);
      toast.error(t(`${CP}.toasts.cliProfileSaveFailed`));
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-4 lg:space-y-6 p-3 sm:p-4 lg:p-7" data-testid="cli-planner-loading">
        <Skeleton className="h-8 lg:h-12 w-48 sm:w-64 lg:w-96" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px]">
          <Skeleton className="h-[200px] lg:h-[280px] rounded-[22px] lg:rounded-[26px]" />
          <Skeleton className="h-[200px] lg:h-[280px] rounded-[22px] lg:rounded-[26px]" />
        </div>
        <Skeleton className="h-[300px] lg:h-[400px] rounded-[22px] lg:rounded-[26px]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4 lg:space-y-[18px] px-3 sm:px-4 lg:px-0 max-w-full overflow-x-hidden" data-testid="cli-planner">
      {/* PAGE TOPBAR */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 lg:gap-[18px]">
        <div className="min-w-0">
          <h1 className="m-0 text-xl sm:text-2xl lg:text-[30px] leading-[1.1] lg:leading-[1.04] font-extrabold tracking-[-0.03em] text-[#0b4f35] dark:text-[#EAF7F0]">
            {t(`${CC}.title`)}
          </h1>
          <p className="text-[#5b7666] dark:text-[#C7DDD0] text-xs sm:text-sm leading-[1.6] max-w-[900px] mt-2 mb-0">
            {t(`${CC}.subtitle`)}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <Button 
            onClick={handleRefreshSync} 
            variant="outline" 
            className="gap-2 border-[rgba(10,95,62,0.16)] font-bold w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10"
            disabled={refreshing}
            data-testid="refresh-sync-btn"
          >
            <RefreshCw size={14} className={`sm:w-4 sm:h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {t(`${CP}.refreshSync`)}
          </Button>
          <Button 
            onClick={handleGoToPaymentPlanner}
            className="gap-2 bg-gradient-to-b from-[#fff8eb] to-[#fdf2dc] text-[#9b7427] border border-[rgba(178,138,52,0.16)] font-bold w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10"
            data-testid="go-to-payment-planner-btn"
          >
            <ExternalLink size={14} className="sm:w-4 sm:h-4" />
            {t(`${CP}.goToPaymentPlanner`)}
          </Button>
          <Button 
            onClick={() => toast.info(t(`${CP}.runAiRecommendationToast`))}
            className="gap-2 bg-gradient-to-br from-[#11875a] to-[#27c57f] text-white font-bold shadow-[0_12px_30px_rgba(17,135,90,0.22)] w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10"
          >
            <Sparkles size={14} className="sm:w-4 sm:h-4" />
            {t(`${CP}.runAiRecommendation`)}
          </Button>
        </div>
      </div>
      
      {/* MASTER HERO */}
      <section className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.95fr] gap-4 lg:gap-[18px]">
        {/* Hero Main */}
        <div className="p-4 lg:p-6 rounded-[22px] lg:rounded-[26px] bg-gradient-to-b from-[rgba(255,255,255,0.97)] to-[rgba(255,255,255,0.92)] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_16px_38px_rgba(11,79,53,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.35)] backdrop-blur-[14px]">
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#e8f8ef] dark:bg-[rgba(16,185,129,0.18)] text-[#0f6a47] dark:text-emerald-300 text-xs font-bold border border-[rgba(17,106,71,0.10)] dark:border-[rgba(16,185,129,0.20)] mb-[14px]">
            <TrendingUp size={14} />
            {t(`${CP}.upstreamBadge`)}
          </div>
          <h2 className="m-0 mb-[10px] text-xl lg:text-[28px] text-[#0b4f35] dark:text-[#EAF7F0] leading-[1.12] lg:leading-[1.08] tracking-[-0.02em] font-extrabold">
            {t(`${CP}.heroTitle`)}
          </h2>
          <p className="m-0 text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] text-sm leading-[1.65] max-w-[780px]">
            {t(`${CP}.heroBody`)}
          </p>
          
          <div className="flex gap-2 lg:gap-3 flex-wrap mt-4 lg:mt-[18px]">
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              {t(`${CP}.primarySourceLabel`)} <strong>{t(`${PP}.title`)}</strong>
            </div>
            {selectedAccount && (
              <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)] max-w-full truncate">
                {t(`${CP}.selectedCardLabel`)} <strong className="truncate">{selectedAccount.account_name}</strong>
              </div>
            )}
            {selectedAccount && (
              <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
                {t(`${CP}.suggestedAskLabel`)}{' '}
                <strong>
                  {formatCurrency(Math.round(selectedAccount.limit * 1.4 / 500) * 500)} – {formatCurrency(Math.round(selectedAccount.limit * 1.6 / 500) * 500)}
                </strong>
              </div>
            )}
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              {t(`${CP}.daysUntilEligibleLabel`)} <strong>{selectedAccount?.readiness?.wait_days || 0}</strong>
            </div>
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              {t(`${CP}.syncedFieldsLabel`)} <strong>8</strong>
            </div>
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              {t(`${CP}.missingCliFieldsLabel`)} <strong>5</strong>
            </div>
          </div>
          
          <div className="flex gap-2 lg:gap-3 flex-wrap mt-3 lg:mt-[18px]">
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">{t(`${CP}.workflowStep1`)}</div>
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">{t(`${CP}.workflowStep2`)}</div>
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">{t(`${CP}.workflowStep3`)}</div>
            <div className="p-[9px_12px] lg:p-[11px_14px] rounded-xl lg:rounded-[15px] bg-white dark:bg-[rgba(10,38,28,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] text-xs lg:text-[13px] text-[#133222] dark:text-[#C7DDD0] font-semibold shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">{t(`${CP}.workflowStep4`)}</div>
          </div>
        </div>
        
        {/* Hero Side */}
        <div className="p-4 lg:p-6 rounded-[22px] lg:rounded-[26px] bg-gradient-to-b from-[rgba(255,255,255,0.97)] to-[rgba(255,255,255,0.92)] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_16px_38px_rgba(11,79,53,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.35)] backdrop-blur-[14px] relative overflow-hidden cli-hero-side"
        >
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#e8f8ef] dark:bg-[rgba(16,185,129,0.18)] text-[#0f6a47] dark:text-emerald-300 text-xs font-bold border border-[rgba(17,106,71,0.10)] dark:border-[rgba(16,185,129,0.20)] mb-[14px]">
            <Target size={14} />
            {t(`${CP}.bestNextAction`)}
          </div>
          <h3 className="m-0 mb-[10px] text-[22px] text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em] font-extrabold">
            {selectedAccount
              ? t(`${CP}.bestNextActionTitleWithCard`, { name: selectedAccount.account_name })
              : t(`${CP}.addCardsForRecommendations`)}
          </h3>
          <p className="m-0 text-[#5b7666] dark:text-[#C7DDD0] dark:text-[#93AE9F] text-sm leading-[1.65]">
            {selectedAccount
              ? t(`${CP}.bestNextActionBodyWithCard`)
              : t(`${CP}.bestNextActionBodyEmpty`)}
          </p>
          
          <div className="flex gap-3 flex-wrap mt-[18px]">
            <SoftBadge variant="success">
              {t(`${CP}.confidence`, {
                level: selectedAccount?.readiness?.score >= 70
                  ? t(`${CP}.confidenceHigh`)
                  : t(`${CP}.confidenceMedium`),
              })}
            </SoftBadge>
            <SoftBadge variant="neutral">
              {t(`${CP}.verifiedOn`, { date: formatDate(new Date().toISOString()) })}
            </SoftBadge>
            <SoftBadge variant="gold">{t(`${CP}.sourceOfficialInternal`)}</SoftBadge>
          </div>
          
          <div className="flex gap-3 flex-wrap mt-[14px]">
            <SoftBadge variant="success">{t(`${CP}.badgeLessDuplicate`)}</SoftBadge>
            <SoftBadge variant="success">{t(`${CP}.badgeBetterAccountability`)}</SoftBadge>
            <SoftBadge variant="gold">{t(`${CP}.badgeMoreSpecialist`)}</SoftBadge>
          </div>
        </div>
      </section>
      
      {/* LIVE PLANNER STATE - Shows when accounts exist */}
      {accounts.length > 0 && (
        <section>
          {/* SYNC STATS */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-[14px] mb-4 lg:mb-[18px]">
            <MetricCard title={t(`${CP}.syncedCards`)} value={accounts.length} subtitle={t(`${CP}.syncedCardsSubtitle`)} />
            <MetricCard title={t(`${CP}.sharedFieldsSynced`)} value="8" subtitle={t(`${CP}.sharedFieldsSyncedSubtitle`)} />
            <MetricCard title={t(`${CP}.cliOnlyFieldsMissing`)} value="5" subtitle={t(`${CP}.cliOnlyFieldsMissingSubtitle`)} />
            <MetricCard title={t(`${CP}.lastSync`)} value={lastSyncTime ? (
              lastSyncTime.toDateString() === new Date().toDateString() 
                ? `${lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` 
                : lastSyncTime.toLocaleDateString([], { month: 'short', day: 'numeric' })
            ) : t(`${CP}.lastSyncNow`)} subtitle={t(`${CP}.lastSyncSubtitle`)} />
          </section>
          
          {/* SYNC BANNER */}
          <section className="flex items-start justify-between gap-4 p-[18px_20px] mb-[18px] rounded-[20px] bg-gradient-to-b from-[rgba(255,255,255,0.95)] to-[rgba(247,251,248,0.95)] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
            <div>
              <h4 className="m-0 mb-[6px] text-[15px] text-[#0b4f35] dark:text-[#EAF7F0] font-extrabold">{t(`${CP}.primaryCardDataSource`)}</h4>
              <p className="m-0 text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6]">
                {t(`${CP}.primaryCardDataSourceBody`)}
              </p>
            </div>
            <SoftBadge variant="success">{t(`${CP}.sourcePaymentPlanner`)}</SoftBadge>
          </section>
          
          {/* SYNC LOGIC ROW */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[18px] mb-4 lg:mb-[18px]">
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${CP}.syncedFromPaymentPlanner`)}</h5>
              <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                {t(`${CP}.syncedFromPaymentPlannerBody`)}
              </p>
              
              <div className="grid gap-[14px] mt-[14px]">
                <ListRow title={t(`${CP}.listSelectedCard`)} badge={t(`${CP}.badgeSynced`)} badgeVariant="success">
                  {t(`${CP}.selectedCardRow`, { name: selectedAccount?.account_name })}
                </ListRow>
                <ListRow title={t(`${CP}.listLastSync`)} badge={t(`${CP}.badgeFields`, { count: 8 })} badgeVariant="neutral">
                  {t(`${CP}.lastSyncRow`)}
                </ListRow>
                <ListRow title={t(`${CP}.listNextStep`)} badge={t(`${CP}.badgeMissing`, { count: 5 })} badgeVariant="gold">
                  {t(`${CP}.nextStepRow`)}
                </ListRow>
              </div>
            </div>
            
            <div className="p-[18px] rounded-[22px] bg-gradient-to-b from-white to-[#fafdfb] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.15)] shadow-[0_8px_20px_rgba(11,79,53,0.05)] dark:shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
              <h5 className="m-0 mb-2 text-[#0b4f35] dark:text-[#EAF7F0] text-lg tracking-[-0.02em] font-extrabold">{t(`${CP}.cliSyncLogic`)}</h5>
              <p className="text-[#5b7666] dark:text-[#C7DDD0] text-[13px] leading-[1.6] m-0">
                {t(`${CP}.cliSyncLogicBody`)}
              </p>
              
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse text-[13px] overflow-hidden rounded-[16px] bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[rgba(10,95,62,0.10)] dark:border-[rgba(142,218,182,0.12)]">
                  <thead>
                    <tr>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${CP}.tableButton`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${CP}.tableDestination`)}</th>
                      <th className="p-[14px_16px] text-left border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.10)] text-xs text-[#7c9888] dark:text-[#93AE9F] font-bold bg-[#fbfdfb] dark:bg-[rgba(8,32,24,0.50)] uppercase tracking-[0.04em]">{t(`${CP}.tableExpectedResult`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.addCardInPaymentPlanner`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.destinationAddCard`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.resultCardSyncsBack`)}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.goToPaymentPlanner`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.destinationOverview`)}</td>
                      <td className="p-[14px_16px] border-b border-[rgba(10,95,62,0.06)] dark:border-[rgba(142,218,182,0.08)]">{t(`${CP}.resultReviewPaydown`)}</td>
                    </tr>
                    <tr className="dark:text-[#C7DDD0]">
                      <td className="p-[14px_16px]">{t(`${CP}.completeCliProfile`)}</td>
                      <td className="p-[14px_16px]">{t(`${CP}.destinationCliPlanner`)}</td>
                      <td className="p-[14px_16px]">{t(`${CP}.resultFinishCliFields`)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* FULL CLI COCKPIT */}
          <section>
            <div className="flex items-center justify-between gap-3 mb-[14px]">
              <div>
                <h3 className="m-0 text-[21px] text-[#0b4f35] dark:text-[#EAF7F0] tracking-[-0.02em] font-extrabold">{t(`${CP}.fullCockpitTitle`)}</h3>
                <p className="m-0 text-[#5b7666] dark:text-[#C7DDD0] text-[13px]">{t(`${CP}.fullCockpitSubtitle`)}</p>
              </div>
              <SoftBadge variant="success">{t(`${CP}.liveSyncedCardSelected`)}</SoftBadge>
            </div>
            
            {/* PLANNER STATS */}
            <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-[14px] mb-4 lg:mb-[18px]">
              <MetricCard title={t(`${CS}.readyNow`)} value={summary.ready_now || 0} subtitle={t(`${CS}.readyNowSubtitle`)} trend={t(`${CS}.trendStrong`)} trendVariant="green" />
              <MetricCard title={t(`${CS}.almostReady`)} value={summary.almost_ready || 0} subtitle={t(`${CS}.almostReadySubtitle`)} trend={t(`${CS}.trendClose`)} trendVariant="gold" />
              <MetricCard title={t(`${CS}.needsPaydown`)} value={summary.needs_paydown || 0} subtitle={t(`${CS}.needsPaydownSubtitle`)} trend={t(`${CS}.trendAction`)} trendVariant="red" />
              <MetricCard title={t(`${CS}.cooldownActive`)} value={summary.cooldown_active || 0} subtitle={t(`${CS}.cooldownActiveSubtitle`)} trend={t(`${CS}.trendWait`)} trendVariant="gold" />
              <MetricCard title={t(`${CS}.manualReview`)} value={summary.manual_review || 0} subtitle={t(`${CS}.manualReviewSubtitle`)} trend={t(`${CS}.trendCheck`)} trendVariant="gold" />
              <MetricCard title={t(`${CS}.autoIncreaseWatchlist`)} value="2" subtitle={t(`${CS}.autoIncreaseWatchlistSubtitle`)} trend={t(`${CS}.trendWatch`)} trendVariant="green" />
            </section>
            
            {/* MAIN ACCOUNT CARD */}
            <div className="grid gap-[18px]">
              {selectedAccount && (
                <AccountPlannerCard 
                  account={selectedAccount}
                  isSelected={true}
                  onSelect={() => {}}
                  onLogEvent={handleLogEvent}
                  onUpdateCLI={handleUpdateCLI}
                />
              )}
              
              {/* WATCHLIST */}
              {watchlistAccounts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {watchlistAccounts.map(account => (
                    <WatchlistCard key={account.card_id} account={account} />
                  ))}
                </div>
              )}
            </div>
          </section>
        </section>
      )}
      
      {/* EMPTY STATE - Shows when no accounts exist */}
      {accounts.length === 0 && (
        <EmptyState onAddCard={handleAddCardInPaymentPlanner} onQuickAdd={handleQuickAdd} />
      )}
    </div>
  );
};

export default CLIPlanner;
